import { Fragment, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, Hotel, Luggage, Mountain, Send } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/site/Reveal";
import { Btn } from "@/components/site/Btn";
import { useLang } from "@/lib/lang";
import { useDemoModal } from "@/components/site/DemoModal";

const REASON_ICONS = [Hotel, Luggage, Mountain];

/* Police manuscrite chargée uniquement lorsque cette page est affichée (aucun style global modifié). */
const useScriptFont = () => {
  useEffect(() => {
    if (document.getElementById("apropos-script-font")) return;
    const link = document.createElement("link");
    link.id = "apropos-script-font";
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Caveat:wght@600;700&display=swap";
    document.head.appendChild(link);
  }, []);
};

/* Illustration line-art propre à la page À propos : smartphone (symbole officiel du projet), hôtel, arbre, pin. */
const MissionIllustration = () => (
  <svg viewBox="0 0 420 340" className="h-auto w-full max-w-md" role="img" aria-hidden="true">
    {/* Forme organique beige */}
    <path
      d="M210 46 C296 42 368 96 374 172 C380 252 316 314 216 318 C120 322 52 268 50 184 C48 102 126 50 210 46 Z"
      fill="#F5EACF"
    />
    {/* Éclats orange */}
    <path
      d="M0 -9 C1.5 -2.5 2.5 -1.5 9 0 C2.5 1.5 1.5 2.5 0 9 C-1.5 2.5 -2.5 1.5 -9 0 C-2.5 -1.5 -1.5 -2.5 0 -9 Z"
      transform="translate(98 78)"
      fill="#F99D2F"
    />
    <path
      d="M0 -6 C1 -1.8 1.8 -1 6 0 C1.8 1 1 1.8 0 6 C-1 1.8 -1.8 1 -6 0 C-1.8 -1 -1 -1.8 0 -6 Z"
      transform="translate(130 56)"
      fill="#F99D2F"
    />
    {/* Ligne de sol */}
    <line x1="92" y1="292" x2="400" y2="292" stroke="#0C1B47" strokeWidth="3" strokeLinecap="round" />
    {/* Smartphone */}
    <rect x="104" y="94" width="108" height="198" rx="20" fill="#FFFFFF" stroke="#0C1B47" strokeWidth="4" />
    <rect x="146" y="107" width="24" height="6" rx="3" fill="#0C1B47" />
    <image href="/favicon.svg" x="130" y="138" width="56" height="56" />
    <rect x="136" y="208" width="44" height="5" rx="2.5" fill="#F99D2F" />
    <rect x="140" y="272" width="36" height="4" rx="2" fill="#0C1B47" />
    {/* Hôtel */}
    <rect x="272" y="110" width="34" height="20" rx="4" fill="#FFFFFF" stroke="#0C1B47" strokeWidth="3.5" />
    <rect x="240" y="128" width="98" height="164" rx="6" fill="#FFFFFF" stroke="#0C1B47" strokeWidth="4" />
    {[256, 282, 308].map((x) =>
      [148, 178, 208].map((y) => (
        <rect
          key={`${x}-${y}`}
          x={x}
          y={y}
          width="14"
          height="14"
          rx="3"
          fill={x === 282 && y === 178 ? "#EAF1FF" : "none"}
          stroke="#0C1B47"
          strokeWidth="2.5"
        />
      ))
    )}
    <rect x="274" y="254" width="28" height="38" rx="4" fill="#EAF1FF" stroke="#0C1B47" strokeWidth="3" />
    {/* Pin de localisation */}
    <path
      d="M346 254 C354 254 360 260.5 360 268.5 C360 278 355 287 346 302 C337 287 332 278 332 268.5 C332 260.5 338 254 346 254 Z"
      fill="#2451E8"
    />
    <circle cx="346" cy="268.5" r="5" fill="#FFFFFF" />
    {/* Arbre */}
    <line x1="386" y1="292" x2="386" y2="248" stroke="#0C1B47" strokeWidth="3.5" strokeLinecap="round" />
    <circle cx="386" cy="226" r="24" fill="none" stroke="#0C1B47" strokeWidth="3.5" />
    <path d="M376 226 q10 -10 20 0" fill="none" stroke="#0C1B47" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);

export default function APropos() {
  const { t } = useTranslation();
  const lang = useLang();
  const openDemo = useDemoModal();
  const reasons = t("apropos.raison_items", { returnObjects: true });
  useScriptFont();

  return (
    <>
      <Seo title={t("apropos.seo_title")} description={t("apropos.seo_desc")} lang={lang} />

      {/* 1 — À propos d'Ostelo */}
      <section className="bg-white px-6 pb-16 pt-[60px] lg:px-10 lg:pb-24 lg:pt-[83px]" data-testid="about-intro">
        <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-ostelo-blue">{t("apropos.intro_overline")}</p>
            <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-ostelo-deep sm:text-5xl lg:text-6xl">
              {t("apropos.intro_title_1")}
              <br />
              {t("apropos.intro_title_2")}
              <br />
              <span className="mt-2 inline-block font-semibold leading-[1.05] text-ostelo-blue [font-family:'Caveat','Fraunces',cursive] text-5xl sm:text-6xl lg:text-7xl">
                {t("apropos.intro_title_script")}
                <span className="text-ostelo-orange">.</span>
              </span>
            </h1>
            <p className="mt-7 max-w-md text-base leading-relaxed text-ostelo-ink/65 md:text-lg">
              {t("apropos.intro_sub")}
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="overflow-hidden rounded-[2rem] shadow-[0_24px_60px_rgb(12,27,71,0.12)]">
              <img
                src="/assets/about/team-1080.webp"
                srcSet="/assets/about/team-640.webp 640w, /assets/about/team-1080.webp 1080w, /assets/about/team-1600.webp 1600w"
                sizes="(min-width: 1024px) 44vw, calc(100vw - 3rem)"
                width="1600"
                height="893"
                alt={t("apropos.team_alt")}
                fetchPriority="high"
                className="h-full w-full object-cover"
                data-testid="about-team-photo"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* 2 — Notre raison d'être */}
      <section className="px-6 pb-10 lg:px-10" data-testid="about-raison">
        <Reveal className="mx-auto max-w-7xl">
          <div className="rounded-[2.5rem] bg-ostelo-sky px-8 py-14 sm:px-12 lg:px-16 lg:py-16">
            <div className="grid items-center gap-12 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-16">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-ostelo-blue">{t("apropos.raison_overline")}</p>
                <h2 className="mt-4 font-display text-3xl font-extrabold leading-[1.12] tracking-tight text-ostelo-deep sm:text-4xl">
                  {t("apropos.raison_title_1")}
                  <br />
                  {t("apropos.raison_title_2")}{" "}
                  <span className="text-ostelo-blue">{t("apropos.raison_title_accent")}</span>
                </h2>
              </div>
              <div className="flex flex-col items-center gap-6 md:flex-row md:items-start md:gap-3 lg:gap-5">
                {reasons.map((item, i) => {
                  const Icon = REASON_ICONS[i];
                  return (
                    <Fragment key={item.title}>
                      {i > 0 && (
                        <ArrowRight
                          size={20}
                          strokeWidth={2.5}
                          className="mt-2 shrink-0 rotate-90 text-ostelo-orange md:mt-12 md:rotate-0"
                          aria-hidden="true"
                        />
                      )}
                      <div className="flex-1 text-center" data-testid={`about-raison-item-${i}`}>
                        <Icon size={44} strokeWidth={1.4} className="mx-auto text-ostelo-deep" aria-hidden="true" />
                        <h3 className="mt-4 font-display text-lg font-bold text-ostelo-deep">{item.title}</h3>
                        <p className="mx-auto mt-2 max-w-60 text-sm leading-relaxed text-ostelo-ink/60">{item.desc}</p>
                      </div>
                    </Fragment>
                  );
                })}
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* 3 — Notre mission */}
      <section className="px-6 py-10 lg:px-10" data-testid="about-mission">
        <Reveal className="mx-auto max-w-7xl">
          <div className="rounded-[2.5rem] bg-[#FBF4E3] px-8 py-14 sm:px-12 lg:px-16 lg:py-16">
            <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-ostelo-orange">{t("apropos.mission_overline")}</p>
                <h2 className="mt-4 font-display text-3xl font-extrabold leading-[1.12] tracking-tight text-ostelo-deep sm:text-4xl">
                  {t("apropos.mission_title_1")}
                  <br />
                  {t("apropos.mission_title_2")}{" "}
                  <span className="text-ostelo-orange">{t("apropos.mission_title_accent")}</span>
                </h2>
                <p className="mt-6 max-w-lg text-base leading-relaxed text-ostelo-ink/65">
                  {t("apropos.mission_text")}
                </p>
              </div>
              <div className="flex justify-center max-md:hidden" data-testid="about-mission-illustration">
                <MissionIllustration />
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* 4 — CTA Demander une démo (action existante réutilisée à l'identique) */}
      <section className="px-6 pb-28 pt-10 lg:px-10" data-testid="about-cta">
        <Reveal className="mx-auto max-w-7xl">
          <div className="flex flex-col items-start gap-8 rounded-[2.5rem] bg-ostelo-deep px-8 py-12 sm:px-12 lg:flex-row lg:items-center lg:gap-12 lg:px-16 lg:py-14">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-white">
              <Send size={26} className="text-ostelo-blue" aria-hidden="true" />
            </div>
            <div className="flex-1">
              <h2 className="font-display text-2xl font-extrabold tracking-tight text-white sm:text-3xl">
                {t("apropos.cta_title")}
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-white/70 sm:text-base">
                {t("apropos.cta_sub_1")}{" "}
                <span className="font-semibold text-ostelo-orange">{t("apropos.cta_sub_accent")}</span>{" "}
                {t("apropos.cta_sub_2")}
              </p>
            </div>
            <Btn variant="accent" testId="about-cta-demo-button" className="shrink-0" onClick={openDemo}>
              {t("common.cta_demo")}
              <ArrowRight size={16} />
            </Btn>
          </div>
        </Reveal>
      </section>
    </>
  );
}
